import {
  GroupedVersionCredits,
  LabelRelease,
  ReleaseType,
  ReleaseVersionContribution,
  SoundCloudRelease,
  TuneCoreRelease,
} from 'types/releases';
import { createSelector } from 'reselect';
import { onlyUnique } from 'app/utils/utils';
import { AppState } from 'app/store';

export const getRelease = (state: AppState) => state.releases.release;

export const getReleaseGuid = (state: AppState) => state.releases.release?.guid;

export const getReleases = (state: AppState) => state.releases.releases;

export const getCreateReleaseLink = (state: AppState) =>
  state.auth.services?.['releases-2']?._links.createRelease;

export const getListReleasesLink = (state: AppState) =>
  state.auth.services?.['releases-2']?._links.listReleases;

export const getReleaseLink = (state: AppState) =>
  state.auth.services?.['releases-2']?._links.getRelease;

export const getReleaseListNextLink = (state: AppState) =>
  state.releases._releasesListLinks?.next;

export const getReleaseSongTitleUpdateLink = (state: AppState) =>
  state.releases.release?._links?.updateReleaseSongOverrides;

export const getUpdateVersionSongLink = (state: AppState) =>
  state.releases.releaseVersion?._links.updateReleaseVersionSong;

export const getUpdateReleaseVersionLink = (state: AppState) =>
  state.releases.releaseVersion?._links.updateReleaseVersion;

export const getUpdateReleaseVersionAudioLink = (state: AppState) =>
  state.releases.releaseVersion?._links.updateReleaseVersionAudio;

export const getUpdateVersionSongValidations = (state: AppState) =>
  state.releases.updateVersionSongValidations;

export const getUpdateReleaseVersionValidations = (state: AppState) =>
  state.releases.updateReleaseVersionValidations;

export const getUpdateSCVersionAudioValidations = (state: AppState) =>
  state.releases.updateSCVersionAudioValidations;

export const getReleaseVersionMetadataUpdateLink = (state: AppState) =>
  state.releases.release?._links?.updateReleaseVersionMetadata;

export const getReleaseVersionDeleteLink = (state: AppState) =>
  state.releases.release?._links?.deleteReleaseVersion;

export const getUpdateReleaseLink = (state: AppState) =>
  state.releases.release?._links?.updateRelease;

export const getReleaseExportLink = (state: AppState) =>
  state.releases.release?._links?.exportRelease;

export const getUpdateReleaseSongTitleValidations = (state: AppState) =>
  state.releases.updateReleaseSongTitleValidations;

export const getUpdateReleaseValidations = (state: AppState) =>
  state.releases.updateReleaseValidations;

export const getReleaseVersionLink = (state: AppState) =>
  state.releases.release?._links?.getReleaseVersion?.href;

export const getReleaseVersion = (state: AppState) =>
  state.releases.releaseVersion;

export const getCreateReleaseVersionLink = (state: AppState) =>
  state.releases.release?._links?.createReleaseVersion;

export const getReleaseVersionSyncLink = (state: AppState) =>
  state.releases.releaseVersion?._links.syncReleaseVersion;

export const getVersionTypeDDLink = (state: AppState) =>
  state.auth.services?.['releases-2']?._links.releaseVersionTypesDDL;

export const getVersionTypes = (state: AppState) =>
  state.releases.releaseVersionTypes;

const getReleaseVersionContributions = (state: AppState) =>
  state.releases.releaseVersion?.contributions || [];

const groupReleaseVersionContributions = (
  contributions: ReleaseVersionContribution[]
) => {
  const contributionsIds = contributions.map((el) => {
    const isSessionUser = Boolean(el.snapshot.contributor.userGuid);
    return isSessionUser
      ? el.snapshot.contributor.userGuid
      : el.contributionGuid;
  });
  const uniqueIds = contributionsIds.filter(onlyUnique);
  const result: GroupedVersionCredits[] = uniqueIds.map((uniqueId) => {
    const filtered = contributions.filter((el) => {
      const isSessionUser = Boolean(el.snapshot.contributor.userGuid);
      return isSessionUser
        ? el.snapshot.contributor.userGuid === uniqueId
        : el.contributionGuid === uniqueId;
    });
    return {
      contribution: filtered[0],
      contributions: filtered,
    };
  });
  return result;
};

export const getGroupedReleaseVersionContributions = createSelector(
  [getReleaseVersionContributions],
  groupReleaseVersionContributions
);

export const getVersionSyncStatus = (state: AppState, versionId: string) =>
  state.releases.syncStatus[versionId];

export const getVersionCreditsUpdateLink = (state: AppState) =>
  state.releases.releaseVersion?._links.updateReleaseVersionContribution;

export const getCreateReleaseValidations = (state: AppState) =>
  state.releases.createReleaseValidations;

export const getDeleteReleaseSongLink = (state: AppState) =>
  state.releases.release?._links?.deleteReleaseSong;

export const getDeleteReleaseLink = (state: AppState) =>
  state.auth.services?.['releases-2']?._links.deleteRelease;

export const getMoveReleaseVersionLink = (state: AppState) =>
  state.releases.release?._links?.moveReleaseVersion?.href;

export const getCreateInitialReleaseVersionLink = (state: AppState) =>
  state.auth.services?.['releases-2']?._links.createReleaseVersion;

export const getListVersionSongAudioLink = (state: AppState) =>
  state.releases.releaseVersion?._links.listAudioBySong;

export const getReplacePrimaryAudioLink = (state: AppState) =>
  state.releases.releaseVersion?._links.replacePrimaryAudio;

export const getCurrentlyReplacingAudio = (state: AppState) =>
  state.releases.currentlyReplacingAudio;

export const getFetchAudioLink = (state: AppState) =>
  state.auth.services?.songs?._links.getAudio;

export const getSoundCloudPlaylistTypesDDLLink = (state: AppState) =>
  state.auth.services?.['releases-2']?._links.soundCloudPlaylistTypesDDL;

export const getSoundCloudPlaylistTypes = (state: AppState) =>
  state.releases.playlistTypesList;

export const getPatchReleaseExportLink = (state: AppState) =>
  state.releases.release?._links?.updateReleaseExport;

export const getUpdateSoundCloudReleaseExportLink = (state: AppState) =>
  state.releases.release?._links?.exportReleaseExport;

export const getReleaseSongOrVersionCount = (state: AppState) => {
  const { release } = state.releases;

  if (release?.type === ReleaseType.LABEL) {
    return (release as LabelRelease).songs.length;
  }

  if (release?.type === ReleaseType.SOUNDCLOUD) {
    return (release as SoundCloudRelease).versions.length;
  }

  if (release?.type === ReleaseType.TUNECORE) {
    return (release as TuneCoreRelease).versions.length;
  }

  return -1;
};

export const getSoundCloudReleaseExportStatus = (state: AppState) =>
  (state.releases.release as SoundCloudRelease)?.isReadyForExportReason;

export const getTuneCoreSuccessDialog = (state: AppState) =>
  state.releases.tuneCoreSuccessDialog;

export const getTuneCoreFailureDialog = (state: AppState) =>
  state.releases.tuneCoreFailureDialog;

export const getListReleaseVersionsLink = (state: AppState) =>
  state.releases?.release?._links?.listReleaseVersions;

export const getDisabledAudios = (state) => {
  if (
    !state.releases?.release ||
    state.releases?.release?.type !== ReleaseType.TUNECORE
  ) {
    return undefined;
  }

  return state.releases?.release?.originalAudioGuids
    ? {
        audios: state.releases.release.originalAudioGuids,
        reason: 'Audio file already added to release',
      }
    : undefined;
};

export const showLabelV2Release = (state: AppState) =>
  state.releases?.createReleaseValidations?.type.enum.includes('LabelV2');

export const getIsAudioUploadSupported = (state: AppState) => (
  releaseGuid = ''
) => {
  const releaseExport = state.releases.export;
  if (releaseExport?.releaseGuid === releaseGuid) {
    return releaseExport.isAudioUploadSupported;
  }

  return true;
};
